import {ECOMMERCE_API_BASE_URL} from '../config';
import {teamFetch} from './teamTransport';
import {apiErrorMessage,readErrorBody,NETWORK_ERROR_MESSAGE} from './errorMessage';
import {preparePrivateImage} from '../utils/imageMetadata';

export type MediaUploadTicket={upload_url:string;key:string;token:string;ticket:string};

// One queue across avatars, product photos, live media and the capture outbox.
export const MEDIA_UPLOAD_CONCURRENCY=3;
let activeUploads=0;
const uploadWaiters:Array<()=>void>=[];
function acquireUpload(signal?:AbortSignal):Promise<()=>void> {
    return new Promise((resolve,reject)=>{
        const cancel=()=>{const index=uploadWaiters.indexOf(start);if(index>=0)uploadWaiters.splice(index,1);reject(new DOMException('Upload cancelled','AbortError'));};
        const start=()=>{
            signal?.removeEventListener('abort',cancel);
            if(signal?.aborted){cancel();return;}
            activeUploads++;
            let released=false;
            resolve(()=>{if(released)return;released=true;activeUploads--;uploadWaiters.shift()?.();});
        };
        if(signal?.aborted){cancel();return;}
        signal?.addEventListener('abort',cancel,{once:true});
        if(activeUploads<MEDIA_UPLOAD_CONCURRENCY)start();else uploadWaiters.push(start);
    });
}

/** Only bytes and the scoped storage token go to object storage. Never ERP credentials. */
export async function uploadMedia(file:Blob,ticket:MediaUploadTicket,signal?:AbortSignal,name='photo'):Promise<void> {
    const url=new URL(ticket.upload_url);
    if(url.protocol!=='https:'||url.hostname!=='upload-z2.qiniup.com')throw new Error('上传地址无效');
    const scope=localStorage.getItem('supplier_portal_session');
    const release=await acquireUpload(signal);
    if(scope!==localStorage.getItem('supplier_portal_session')){release();throw new DOMException('Session changed','AbortError');}
    const controller=new AbortController();
    const cancel=()=>controller.abort();signal?.addEventListener('abort',cancel,{once:true});
    if(signal?.aborted)cancel();
    let timedOut=false;
    const timeout=setTimeout(()=>{timedOut=true;cancel();},120000);
    try {
        const body=new FormData();body.set('token',ticket.token);body.set('key',ticket.key);body.set('file',file,name);
        const response=await fetch(ticket.upload_url,{method:'POST',body,signal:controller.signal,credentials:'omit'});
        // A lost upload response may be retried against the same immutable key.
        if(!response.ok&&response.status!==614){
            const body=await readErrorBody(response);
            const storageError=body&&typeof body==='object'?(body as {error?:unknown}).error:body;
            // Storage error strings are diagnostic data, not ERP validation errors.
            // Keep the reason while excluding markup, URLs and credential-sized strings.
            const detail=typeof storageError==='string'&&!/[<>]/.test(storageError)?storageError.replace(/https?:\/\/\S+/g,'[地址]').replace(/[A-Za-z0-9_\/+=:-]{40,}/g,'[已隐藏]').slice(0,240):'';
            const reason=detail?`存储服务拒绝上传：${detail}`:response.status===401||response.status===403?'图片上传凭证无效，请重试':response.status===413?'图片过大，请重新上传':response.status>=500?'图片存储服务出错，请稍后重试':'存储服务未接受这张图片，请重试';
            const requestId=response.headers?.get('x-reqid');
            const diagnostic=requestId&&/^[A-Za-z0-9_-]{1,64}$/.test(requestId)?`（请求号 ${requestId}）`:'';
            throw Object.assign(new Error(`图片传输失败（HTTP ${response.status}）：${reason}${diagnostic}`),{status:response.status,source:'storage'});
        }
    } catch(error) {
        if(timedOut&&!signal?.aborted)throw Object.assign(new Error('上传超时，请重试'),{status:408});
        throw error;
    } finally {clearTimeout(timeout);signal?.removeEventListener('abort',cancel);release();}
}

/** Keep business fields and submit only verified tickets in place of file bodies. */
export async function directMediaForm(target:string,form:FormData,signal?:AbortSignal,preserveLegacyBytes=false):Promise<FormData> {
    const scope=localStorage.getItem('supplier_portal_session');
    const check=()=>{if(signal?.aborted||scope!==localStorage.getItem('supplier_portal_session'))throw new DOMException('Session changed or cancelled','AbortError');};
    const entries=Array.from(form.entries());
    const files=entries.filter((entry):entry is [string,File]=>entry[1] instanceof File&&entry[1].size>0);
    const uploaded:string[]=[];
    for(let offset=0;offset<files.length;offset+=20) {
        check();
        // Work on upload copies only, before obtaining size-bound tickets.
        const batch:Array<[string,File]>=[];
        for(const [field,file] of files.slice(offset,offset+20))batch.push([field,preserveLegacyBytes?file:await preparePrivateImage(file)]);
        check();
        const descriptions=batch.map(([field,file])=>({target:new URL(target,window.location.origin).pathname,field,name:file.name,size:file.size,type:file.type}));
        const response=await teamFetch(`${ECOMMERCE_API_BASE_URL}/supplier/media/uploads`,{method:'POST',signal,headers:{'Content-Type':'application/json'},
            body:JSON.stringify({items:descriptions})},()=>new Error(NETWORK_ERROR_MESSAGE));
        if(!response.ok)throw new Error(apiErrorMessage(response.status,await readErrorBody(response)));
        const data=await response.json();
        if(!Array.isArray(data.tickets)||data.tickets.length!==batch.length)throw new Error('未收到上传凭证，请重试');
        check();
        // Settle every in-flight upload before callers may retry or switch accounts.
        const results=await Promise.allSettled(batch.map(async([,file],index)=>{
            check();const ticket:MediaUploadTicket=data.tickets[index];
            await uploadMedia(file,ticket,signal,file.name);check();uploaded[offset+index]=ticket.ticket;
        }));
        const failed=results.find((result):result is PromiseRejectedResult=>result.status==='rejected');
        if(failed)throw failed.reason;
    }
    check();const result=new FormData();
    let fileIndex=0;
    for(const [field,value] of entries) {
        const ticket=value instanceof File&&value.size>0?uploaded[fileIndex++]:undefined;
        if(ticket)result.append(field+'_ticket',ticket);else result.append(field,value);
    }
    return result;
}
